import { useState, useRef, useEffect } from 'react';

// Reusable numeric picker: a text input with a custom dropdown of suggested
// values (one-arrow "picker", all options shown on click — unlike a native
// datalist which filters the list to the current text). Free typing is always
// allowed; the dropdown is purely a convenience.

export interface NumberPickerProps {
  value: number;
  onChange: (v: number) => void;
  /** Suggested values shown in the dropdown (always all of them). */
  options: number[];
  placeholder?: string;
  className?: string;
  min?: number;
  /** Freeze the picker (lot A2-CAP). Defaults to false. */
  disabled?: boolean;
}

export default function NumberPicker({ value, onChange, options, placeholder, className = 'form-input', disabled = false }: NumberPickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="number-picker" style={{ position: 'relative', display: 'inline-block' }}>
      <input
        className={className}
        type="text"
        inputMode="numeric"
        value={value ? String(value) : ''}
        disabled={disabled}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          onChange(Number.isNaN(n) ? 0 : n);
        }}
        placeholder={placeholder}
        autoComplete="off"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Pick a number"
        disabled={disabled}
        onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        style={{
          position: 'absolute', right: 0, top: 0, bottom: 0, width: 22,
          border: 'none', background: 'transparent', cursor: disabled ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-dim)',
        }}
      >
        ▾
      </button>
      {open && !disabled && (
        <div
          className="number-picker-menu"
          style={{
            position: 'absolute', top: '100%', right: 0, zIndex: 30,
            minWidth: 90, maxHeight: 220, overflowY: 'auto',
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 8, marginTop: 4, boxShadow: '0 6px 18px rgba(0,0,0,.3)', padding: 4,
          }}
        >
          {options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={(e) => { e.preventDefault(); onChange(o); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '4px 8px',
                border: 'none', background: o === value ? 'var(--accent-muted)' : 'transparent',
                color: 'var(--text)', borderRadius: 6, cursor: 'pointer', fontSize: 'var(--font-md)',
              }}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
